import React, { useState, useEffect, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import NewSideBar from "../../components/Navigation/NewSideBar";
import useInterview from "../../utils/hooks/useInterview";
import { LayoutContext } from "../../utils/contexts/LayoutContext";

export default function EditQuestionPage() {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams();
  const { fetchOneQuestion, updateOneQuestion } = useInterview();

  const { setPadding } = useContext(LayoutContext);
  useEffect(() => {
    setPadding("p-0");
    return () => {
      setPadding("p-4");
    };
  }, []);

  // Compute “Question N” title from location.state.no
  const no = location.state?.no ?? 0;
  const [title, setTitle] = useState("");
  useEffect(() => {
    setTitle(`Question ${no}`);
  }, [no]);

  // Load existing question data
  useEffect(() => {
    (async () => {
      const res = await fetchOneQuestion(uuid);
      if (res.success) {
        reset({
          QuestionName: res.data.name,
          QuestionBody: res.data.body,
        });
      }
    })();
  }, [uuid, fetchOneQuestion, reset]);

  // Submit handler
  const onSubmit = async (data) => {
    try {
      await updateOneQuestion(uuid, data);
      navigate("/question/question-bank");
    } catch (err) {
      console.error("Update failed:", err);
      alert(err.detail || "Save failed");
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-300">
      <div className="flex h-full">
        <NewSideBar />

        <form
          className="flex flex-col w-screen"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex-1 p-8 bg-white dark:bg-gray-900">
            <button
              type="button"
              className="text-sm text-gray-600 dark:text-gray-300 py-5 hover:underline"
              onClick={() => navigate("/question/question-bank")}
            >
              ← Back
            </button>

            <h2 className="text-2xl font-bold mb-4">{title}</h2>

            <div className="flex flex-col mb-4">
              <Controller
                name="QuestionName"
                control={control}
                defaultValue=""
                rules={{ required: "Question Title is required" }}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="Give your question a title."
                    className="text-m border rounded text-gray-500 dark:bg-gray-800 dark:text-white dark:border-gray-600"
                  />
                )}
              />
              {errors.QuestionName && (
                <p className="text-red-500 text-xs">
                  {errors.QuestionName.message}
                </p>
              )}
            </div>

            <div className="mb-6">
              <label className="block font-semibold mb-1">
                Question Content
              </label>
              <Controller
                name="QuestionBody"
                control={control}
                defaultValue=""
                rules={{ required: "Question Content is required" }}
                render={({ field }) => (
                  <textarea
                    {...field}
                    rows={4}
                    placeholder="Write your question here"
                    className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white dark:border-gray-600"
                  />
                )}
              />
              {errors.QuestionBody && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.QuestionBody.message}
                </p>
              )}
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
